import type { ComputedRef, Ref } from 'vue'
import type { MultiselectSearchOption } from '@shared/ui/fields'

import { useCustomerStore } from '@entities/customer'
import { useListsStore } from '@shared/model'

import { useCustomerCommentsStore } from './store'
import { useCustomerCommentsApi } from '../../api/comments/comments.hook'

interface CustomerCommentsFiltersReturn {
  refOptions: ComputedRef<MultiselectSearchOption[]>
  refInitialValue: ComputedRef<string[]>
  refSelected: Ref<string[]>
  isLoading: ComputedRef<boolean>
  setSelected: (options: MultiselectSearchOption[]) => void
  submitSearch: () => Promise<void>
  clearFilters: () => Promise<void>
}

/**
 * Like the legacy filter, picking disciplines only drafts the filter: "Search" applies it through the
 * URL, "Clear filters" drops it. Options are `Kid (Discipline)` for every discipline of the customer.
 */
export const useCustomerCommentsFilters = (): CustomerCommentsFiltersReturn => {
  const route = useRoute()
  const router = useRouter()
  const customerStore = useCustomerStore()
  const commentsStore = useCustomerCommentsStore()
  const listsStore = useListsStore()

  const refSelected = ref<string[]>([...commentsStore.getFilterDisciplines])

  const refOptions = computed<MultiselectSearchOption[]>(() =>
    customerStore.getCustomerKidDisciplines.map((discipline) => {
      const id = String(discipline.id)
      const code = listsStore.getLists?.discipline?.code?.[discipline.code] || discipline.code

      return {
        id,
        value: id,
        text: `${discipline.kidName} (${code})`,
        selected: refSelected.value.includes(id),
      }
    }))

  const refInitialValue = computed<string[]>(() =>
    refOptions.value.filter(option => commentsStore.getFilterDisciplines.includes(String(option.id))).map(option => option.text))

  const isLoading = computed<boolean>(() => commentsStore.isLoading)

  const setSelected = (options: MultiselectSearchOption[]): void => {
    refSelected.value = options.map(option => String(option.id))
  }

  /** Re-running the current filter refetches instead of being a no-op navigation. */
  const pushQuery = async (disciplines: string[]): Promise<void> => {
    const query = { disciplines: disciplines.length ? disciplines : undefined }

    if (router.resolve({ query }).fullPath === route.fullPath) {
      await useCustomerCommentsApi({ customerId: route.params.id as string })
      return
    }

    await router.push({ query })
  }

  const submitSearch = (): Promise<void> => pushQuery(refSelected.value)

  const clearFilters = (): Promise<void> => {
    refSelected.value = []

    return pushQuery([])
  }

  watch(() => route.query, () => {
    refSelected.value = [...commentsStore.getFilterDisciplines]
  })

  return {
    refOptions,
    refInitialValue,
    refSelected,
    isLoading,
    setSelected,
    submitSearch,
    clearFilters,
  }
}
